import { useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { User, SharedProps } from '../../types'
import UserAvatar from '../../components/shared/UserAvatar'
import Modal from '../../components/shared/Modal'
import Layout from '../../components/Layout'
import { sortPeople } from '../../utils/peopleSort'

interface TeamMember {
  id: number
  user_id: number
  role: string
  user: User
}

interface Team {
  id: number
  name: string
  description: string | null
  meeting_day: string | null
  meeting_time: string | null
  scoreboard_win_mode: 'automatic' | 'manual'
  scoreboard_win_threshold: number
  scoreboard_week_start_day?: string
}

const WEEK_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

interface Props {
  team: Team
  members: TeamMember[]
  available_users: User[]
}

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'facilitator', label: 'Facilitator' },
  { value: 'leader', label: 'Leader' },
]

export default function Settings({ team, members, available_users }: Props) {
  const [teamName, setTeamName] = useState(team.name)
  const [teamDescription, setTeamDescription] = useState(team.description || '')
  const [winMode, setWinMode] = useState(team.scoreboard_win_mode || 'automatic')
  const [winThreshold, setWinThreshold] = useState(team.scoreboard_win_threshold || 80)
  const [weekStartDay, setWeekStartDay] = useState(team.scoreboard_week_start_day || 'monday')
  const [saving, setSaving] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedRole, setSelectedRole] = useState('member')

  const handleSaveTeam = () => {
    setSaving(true)
    router.put(`/teams/${team.id}/settings`, {
      team: { name: teamName, description: teamDescription, scoreboard_win_mode: winMode, scoreboard_win_threshold: winThreshold, scoreboard_week_start_day: weekStartDay }
    }, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  const handleRoleChange = (membershipId: number, newRole: string) => {
    router.put(`/teams/${team.id}/members/${membershipId}`, {
      role: newRole,
    }, { preserveScroll: true })
  }

  const handleRemoveMember = (membershipId: number) => {
    if (!confirm('Are you sure you want to remove this member from the team?')) return
    router.delete(`/teams/${team.id}/members/${membershipId}`, {
      preserveScroll: true,
    })
  }

  const handleAddMember = () => {
    if (!selectedUserId) return
    router.post(`/teams/${team.id}/members`, {
      user_id: selectedUserId,
      role: selectedRole,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setShowAddModal(false)
        setSearchQuery('')
        setSelectedUserId(null)
        setSelectedRole('member')
      },
    })
  }

  // Task #613: sort after filtering so search results stay alphabetical.
  const filteredUsers = sortPeople(available_users.filter(u => {
    const q = searchQuery.toLowerCase()
    return u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
  }))

  const leaderCount = members.filter(m => m.role === 'leader').length

  return (
    <Layout team={{ id: team.id, name: team.name, member_count: members.length }}>
      <Head title={`${team.name} Settings - Pinnacle`} />

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-text-primary font-heading">
            {team.name} — Settings
          </h2>
        </div>

        <div className="bg-white rounded-xl shadow-card border border-stone/10 p-6 mb-8">
          <h3 className="text-lg font-semibold text-text-primary mb-4 font-heading">Team Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Team Name</label>
              <input
                type="text"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
              <textarea
                value={teamDescription}
                onChange={e => setTeamDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red resize-none"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveTeam}
                disabled={saving || !teamName.trim()}
                className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2 px-6 rounded-lg transition-colors font-heading text-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card border border-stone/10 p-6 mb-8">
          <h3 className="text-lg font-semibold text-text-primary mb-4 font-heading">Scoreboard Settings</h3>
          <p className="text-sm text-text-secondary mb-4">
            Configure how "Win the Week" is determined for this team's scoreboard during meetings.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Win Mode</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setWinMode('automatic')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-left transition-colors ${
                    winMode === 'automatic'
                      ? 'border-pinnacle-red bg-pinnacle-red/5'
                      : 'border-stone/20 hover:border-stone/40'
                  }`}
                >
                  <div className="text-sm font-semibold text-text-primary">Automatic</div>
                  <div className="text-sm text-text-secondary mt-0.5">
                    Win/loss is calculated automatically based on the percentage of metrics on target.
                  </div>
                </button>
                <button
                  onClick={() => setWinMode('manual')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-left transition-colors ${
                    winMode === 'manual'
                      ? 'border-pinnacle-red bg-pinnacle-red/5'
                      : 'border-stone/20 hover:border-stone/40'
                  }`}
                >
                  <div className="text-sm font-semibold text-text-primary">Manual</div>
                  <div className="text-sm text-text-secondary mt-0.5">
                    The facilitator marks Won or Lost during each meeting.
                  </div>
                </button>
              </div>
            </div>
            {winMode === 'automatic' && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Win Threshold — {winThreshold}%
                </label>
                <p className="text-sm text-text-secondary mb-2">
                  The team wins the week when at least this percentage of metrics are on target.
                </p>
                <input
                  type="range"
                  min={1}
                  max={100}
                  value={winThreshold}
                  onChange={e => setWinThreshold(Number(e.target.value))}
                  className="w-full accent-pinnacle-red"
                />
                <div className="flex justify-between text-sm text-text-muted mt-1">
                  <span>1%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Week Start Day</label>
              <p className="text-sm text-text-secondary mb-2">
                Which day this team's scoreboard week starts on.
              </p>
              <select
                value={weekStartDay}
                onChange={e => setWeekStartDay(e.target.value)}
                className="w-full sm:w-60 px-3 py-2 border border-stone/30 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red bg-white"
              >
                {WEEK_DAYS.map(day => (
                  <option key={day} value={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveTeam}
                disabled={saving}
                className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2 px-6 rounded-lg transition-colors font-heading text-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card border border-stone/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary font-heading">Team Members</h3>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2 px-4 rounded-lg transition-colors font-heading text-sm flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Member
            </button>
          </div>

          {members.length === 0 ? (
            <p className="text-sm text-text-secondary py-4 text-center">No members yet. Add team members to get started.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone/20">
                    <th className="text-left text-sm font-semibold text-text-secondary uppercase tracking-wider py-3 px-2">Member</th>
                    <th className="text-left text-sm font-semibold text-text-secondary uppercase tracking-wider py-3 px-2">Email</th>
                    <th className="text-left text-sm font-semibold text-text-secondary uppercase tracking-wider py-3 px-2">Role</th>
                    <th className="text-right text-sm font-semibold text-text-secondary uppercase tracking-wider py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortPeople(members).map(member => {
                    const isLastLeader = member.role === 'leader' && leaderCount <= 1
                    return (
                      <tr key={member.id} className="border-b border-stone/10 last:border-0">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-3">
                            <UserAvatar user={member.user} size="sm" />
                            <span className="text-sm font-medium text-text-primary">{member.user.full_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <span className="text-sm text-text-secondary">{member.user.email}</span>
                        </td>
                        <td className="py-3 px-2">
                          <select
                            value={member.role}
                            onChange={e => handleRoleChange(member.id, e.target.value)}
                            disabled={isLastLeader}
                            className="text-sm border border-stone/30 rounded-lg px-2 py-1.5 text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                          >
                            {ROLE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            disabled={isLastLeader}
                            className="text-sm text-[#ED1C24] hover:text-pinnacle-red-dark disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title={isLastLeader ? 'Cannot remove the last leader' : 'Remove member'}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={showAddModal} onClose={() => { setShowAddModal(false); setSearchQuery(''); setSelectedUserId(null) }} title="Add Team Member">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Search Users</label>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-3 py-2 border border-stone/30 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
            />
          </div>

          <div className="max-h-48 overflow-y-auto border border-stone/20 rounded-lg">
            {filteredUsers.length === 0 ? (
              <p className="text-sm text-text-secondary py-4 text-center">No available users found.</p>
            ) : (
              filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => setSelectedUserId(user.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F5F5F5]/50 transition-colors border-b border-stone/10 last:border-0 ${selectedUserId === user.id ? 'bg-pinnacle-red/5 ring-1 ring-pinnacle-red/20' : ''}`}
                >
                  <UserAvatar user={user} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-text-primary truncate">{user.full_name}</p>
                    <p className="text-sm text-text-secondary truncate">{user.email}</p>
                  </div>
                  {selectedUserId === user.id && (
                    <svg className="w-5 h-5 text-pinnacle-red flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>

          {selectedUserId && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Role</label>
              <select
                value={selectedRole}
                onChange={e => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
              >
                {ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowAddModal(false); setSearchQuery(''); setSelectedUserId(null) }}
              className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddMember}
              disabled={!selectedUserId}
              className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2 px-6 rounded-lg transition-colors font-heading text-sm disabled:opacity-50"
            >
              Add Member
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  )
}
